class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    checkCartForUpsell();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function renderUpsellForm(product, isSubscription, cartSellingPlanName) {
  const variants = product?.variants?.filter(v => v.available) || [];
  const firstAvailable = variants[0];
  if (!firstAvailable) return;

  const upsellContainer = document.getElementById('upsell-offer');
  if (!upsellContainer || !variants.length) return;

  const hasMultipleVariants = product.variants.length > 1;

  const planDataScript = document.getElementById('upsell-selling-plans-json');
  let upsellPlans = [];

  if (planDataScript) {
    try {
      upsellPlans = JSON.parse(planDataScript.textContent);
    } catch {}
  }

  let sellingPlanId = null;

  if (isSubscription && cartSellingPlanName && upsellPlans.length) {
    const normalizedCartName = cartSellingPlanName.trim().toLowerCase();

    for (const plan of upsellPlans) {
      const planName = plan.selling_plan_name?.trim().toLowerCase();

      if (
        planName === normalizedCartName ||
        planName.includes(normalizedCartName) ||
        normalizedCartName.includes(planName) ||
        (planName.includes('60 day') && normalizedCartName.includes('60')) ||
        (planName.includes('30 day') && normalizedCartName.includes('30'))
      ) {
        sellingPlanId = plan.selling_plan_id;
        break;
      }
    }
  }

  const originalPrice = firstAvailable.price / 100;
  const discountedPrice = sellingPlanId ? (originalPrice * 0.85).toFixed(2) : originalPrice.toFixed(2);

  const getVariantImage = (variant) =>
    variant.featured_media?.preview_image?.src ||
    variant.featured_image?.src ||
    product.featured_image?.src ||
    product.images?.[0];

  const variantOptionsHTML = hasMultipleVariants
    ? `
      <div class="upsell-select-wrapper">
        <select class="upsell-variant-id upsell-variant-selector">
          ${product.variants.map(v => `
            <option 
              value="${v.id}"
              ${v.available ? '' : 'disabled'}
              ${v.id === firstAvailable.id ? 'selected' : ''}
              data-price="${(v.price / 100).toFixed(2)}"
              data-image="${getVariantImage(v)}"
            >
              ${v.title}${v.available ? '' : ' (Sold out)'}
            </option>`).join('')}
        </select>
      </div>
    `
    : `
      <input type="hidden" class="upsell-variant-id" 
        value="${firstAvailable.id}" 
        data-price="${originalPrice.toFixed(2)}" />
    `;

  upsellContainer.innerHTML = `
    <div class="upsell-box">
      <p class="upsell-message">PAIR WITH</p>
      <div class="upsell-content">
        <div class="upsell-text">
          <h4 class="upsell-title">${product.title}</h4>
          <div class="upsell-price-wrap">
            <span class="upsell-price">$${discountedPrice}</span>
            ${sellingPlanId ? `<span class="upsell-compare-price line-through ml-2 opacity-70">$${originalPrice.toFixed(2)}</span>` : ''}
          </div>
          ${variantOptionsHTML}
          <button class="btn btn-primary upsell-add-btn">Add to Cart</button>
        </div>
        <img
          src="${getVariantImage(firstAvailable)}"
          alt="${product.title}"
          class="upsell-image"
        />
      </div>
    </div>
  `;

  const accordion = document.querySelector('.cart_products-accrodion');
  if (accordion) accordion.style.display = 'none';

  const select = upsellContainer.querySelector('.upsell-variant-selector');
  const img = upsellContainer.querySelector('.upsell-image');
  if (select) {
    select.addEventListener('change', (e) => {
      const selectedOption = e.target.selectedOptions[0];
      const newImg = selectedOption.getAttribute('data-image');
      const basePrice = parseFloat(selectedOption.getAttribute('data-price'));

      if (img && newImg) img.src = newImg;

      if (!isNaN(basePrice)) {
        const newDiscountedPrice = sellingPlanId ? (basePrice * 0.85).toFixed(2) : basePrice.toFixed(2);
        const priceEl = upsellContainer.querySelector('.upsell-price');
        const compareEl = upsellContainer.querySelector('.upsell-compare-price');

        if (priceEl) priceEl.textContent = `$${newDiscountedPrice}`;
        if (compareEl) {
          if (sellingPlanId) {
            compareEl.textContent = `$${basePrice.toFixed(2)}`;
            compareEl.style.display = '';
          } else {
            compareEl.textContent = '';
            compareEl.style.display = 'none';
          }
        }
      }
    });
  }

  upsellContainer.querySelector('.upsell-add-btn')?.addEventListener('click', () => {
    const variantInput = upsellContainer.querySelector('.upsell-variant-id, .upsell-variant-selector');
    const variantId = parseInt(variantInput.value);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          ...(sellingPlanId && { selling_plan: sellingPlanId })
        }]
      })
    })
    .then(() => {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then(res => res.text())
        .then(htmlText => {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          ['cart-drawer-items', '.cart-drawer__footer'].forEach(selector => {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          });

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.classList.add('is-open');
            document.body.classList.add('overflow-hidden');
          }

          setTimeout(checkCartForUpsell, 50);
        });
    });
  });

  upsellContainer.style.display = 'block';
}


function checkCartForUpsell() {
  const upsellDataScript = document.getElementById('upsell-metafields-json');
  if (!upsellDataScript) return;

  const upsellData = JSON.parse(upsellDataScript.textContent || '{}');
  const upsellHandle = upsellData.upsell_handle;
  if (!upsellHandle) return;

  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      if (!cart.items.length) return;

      const firstItem = cart.items[0];
      const isSubscription = !!firstItem.selling_plan_allocation?.selling_plan;
      const cartSellingPlanName = firstItem.selling_plan_allocation?.selling_plan?.name?.trim() || null;

      const existingVariantIds = cart.items.map(item => item.variant_id);

      fetch(`/products/${upsellHandle}.js`)
        .then(res => res.json())
        .then(product => {
          const isAlreadyInCart = product.variants.some(variant =>
            existingVariantIds.includes(variant.id)
          );

          if (isAlreadyInCart) {
            document.getElementById('upsell-offer').style.display = 'none';
            return;
          }

          renderUpsellForm(product, isSubscription, cartSellingPlanName);
        });
    });
}


// 🧼 Hook it in on page/cart updates
document.addEventListener('DOMContentLoaded', () => {
  checkCartForUpsell();
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (['cart-items', 'product-form'].includes(event.source)) {
      setTimeout(checkCartForUpsell, 50);
    }
  });
});

const cartDrawer = document.querySelector('cart-drawer');
if (cartDrawer) {
  cartDrawer.addEventListener('transitionend', () => {
    checkCartForUpsell();
  }, { once: true });
}
